import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Trash2 } from 'lucide-react';
import { useApiErrors } from '@soap/modules';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { ThinkingAnimation } from '../../ThinkingAnimation';
import { getTopicId } from '../utils/topicUtils';

interface RemoveTopicsFromStudentCurriculumDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedTopics: any[];
    buildBreadcrumb: (topic: any) => string;
    onRemoveTopic: (topicId: string) => void;
    onConfirm: () => void;
    selectedStudentFirstName?: string;
    isProcessing?: boolean;
    onProcessingFailed?: () => void;
}

function RemoveTopicsProcessingOverlay({
    topicCount,
    studentCurriculumLabel
}: {
    topicCount: number;
    studentCurriculumLabel: string;
}) {
    const topicLabel = topicCount === 1 ? 'topic' : 'topics';
    return (
        <Dialog open onOpenChange={() => {}}>
            <DialogContent dismissible={false} className="max-w-2xl sm:max-w-2xl">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Removing {topicCount} {topicLabel}
                </h2>
                <p className="text-sm text-gray-600 mb-2">
                    Updating {studentCurriculumLabel}. This may take a few moments…
                </p>
                <ThinkingAnimation message={`Removing ${topicLabel} from curriculum`} />
            </DialogContent>
        </Dialog>
    );
}

export function RemoveTopicsFromStudentCurriculumDialog({
    open,
    onOpenChange,
    selectedTopics,
    buildBreadcrumb,
    onRemoveTopic,
    onConfirm,
    selectedStudentFirstName = '',
    isProcessing = false,
    onProcessingFailed
}: RemoveTopicsFromStudentCurriculumDialogProps) {
    const firstName = selectedStudentFirstName.trim() || 'Student';
    const studentCurriculumLabel = `${firstName}'s Curriculum`;

    const topicsToRemove = useMemo(
        () =>
            selectedTopics.filter((topic) => {
                const id = topic?.e180_TopicId;
                return id != null && String(id).trim() !== '';
            }),
        [selectedTopics]
    );

    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;

    useApiErrors(() => {
        if (!isProcessing) return;
        window.setTimeout(() => onProcessingFailed?.(), 0);
    });

    const scheduleCloseWhenEmpty = useCallback(() => {
        window.setTimeout(() => onOpenChangeRef.current(false), 0);
    }, []);

    useEffect(() => {
        if (!open || isProcessing || topicsToRemove.length > 0) return;
        scheduleCloseWhenEmpty();
    }, [open, isProcessing, topicsToRemove.length, scheduleCloseWhenEmpty]);

    const handleExcludeTopic = useCallback(
        (topicId: string) => {
            const isLast =
                topicsToRemove.length === 1 &&
                (getTopicId(topicsToRemove[0]) ?? String(topicsToRemove[0].e180_TopicId)) === topicId;
            onRemoveTopic(topicId);
            if (isLast) {
                scheduleCloseWhenEmpty();
            }
        },
        [topicsToRemove, onRemoveTopic, scheduleCloseWhenEmpty]
    );

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && isProcessing) return;
        onOpenChange(nextOpen);
    };

    if (!open) return null;

    if (isProcessing) {
        return (
            <RemoveTopicsProcessingOverlay
                topicCount={topicsToRemove.length}
                studentCurriculumLabel={studentCurriculumLabel}
            />
        );
    }

    const topicCount = topicsToRemove.length;
    const topicLabel = topicCount === 1 ? 'topic' : 'topics';

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0 overflow-hidden">
                <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
                    <DialogTitle>Remove Topics from {studentCurriculumLabel}</DialogTitle>
                </DialogHeader>
                <div className="px-6 pb-4 flex-1 min-h-0 overflow-y-auto space-y-3">
                    <p className="text-sm text-gray-600">
                        These {topicCount} {topicLabel} will be removed from {studentCurriculumLabel}. Remove any you
                        want to keep before continuing.
                    </p>
                    <p className="text-sm text-gray-500">
                        To add a topic back later, use Add Topics and find it in the library.
                    </p>
                    <ul className="space-y-2">
                        {topicsToRemove.map((topic) => {
                            const topicId = getTopicId(topic) ?? String(topic.e180_TopicId);
                            const name =
                                topic.e180_Name?.trim() ||
                                topic.e179_Name?.trim() ||
                                'Untitled topic';
                            const breadcrumb = buildBreadcrumb(topic);

                            return (
                                <li
                                    key={topicId}
                                    className="rounded-lg border border-gray-200 bg-white shadow-sm px-4 py-3 flex items-start gap-3"
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-900">{name}</p>
                                        {breadcrumb ? (
                                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{breadcrumb}</p>
                                        ) : null}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleExcludeTopic(topicId)}
                                        className="text-gray-400 hover:text-destructive-action shrink-0 p-1 rounded-md hover:bg-destructive-action/10"
                                        aria-label={`Do not remove ${name}`}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <DialogFooter className="px-6 py-4 border-t border-gray-100 shrink-0">
                    <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={topicCount === 0}
                    >
                        Remove {topicCount} {topicLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
